import { useEffect, Dispatch, SetStateAction } from 'react'
import { WppActionButton, WppIconDownload } from '@wppopen/components-library-react'
import { ApiResponse, FetchReq } from 'hooks/useApi'
import { createCsApiUrlFromEndpoint, downloadCSVFile } from 'utils'
import type { ExportListParams2, JobsListData } from '@contracts'
import { DEFAULT_API_ITEM_LIMITS } from 'utils/constants'

interface DownloadAllJobsButtonProps {
  fetchUrl: URL
  itemCount?: number
  jobExportApiReq: ApiResponse<string>
  fetchJobData?: FetchReq<JobsListData>
  showDownloadError: boolean
  setShouldShowDownloadError: Dispatch<SetStateAction<boolean>>
  isBulkDownloadAction?: boolean
  job_ids?: string[]
  disabled?: boolean
}

function DownloadAllJobsButton({
  fetchUrl,
  fetchJobData,
  itemCount,
  jobExportApiReq,
  setShouldShowDownloadError,
  isBulkDownloadAction = false,
  disabled,
}: DownloadAllJobsButtonProps) {
  const {
    fetchData: fetchExportedJobData,
    data: exportedJobData,
    isLoading: isExportedJobApiLoading,
    error: exportedJobApiError,
  } = jobExportApiReq

  useEffect(() => {
    if (!isExportedJobApiLoading && !exportedJobApiError && exportedJobData) {
      downloadCSVFile(exportedJobData, 'filtered_task_output.csv')
      const newFetchUrl = new URL(fetchUrl)

      // Fetch the first page of jobs again to reset the position
      if (!!fetchJobData) {
        newFetchUrl.searchParams.set('position', '0')
        newFetchUrl.searchParams.set('limit', DEFAULT_API_ITEM_LIMITS.JOBS_API.toString())
        fetchJobData(newFetchUrl)
      }
    }
    if (!isExportedJobApiLoading && exportedJobApiError) {
      setShouldShowDownloadError(true)
    }
  }, [isExportedJobApiLoading])

  const handleDownloadAllClick = () => {
    setShouldShowDownloadError(false)
    const searchParams = new URLSearchParams(fetchUrl.search)

    // decouple the filter inputs from the download all button if it's a bulk download action
    // download all functionality should not be tied to the filter inputs, only download all jobs in the project or the selected jobs
    if (isBulkDownloadAction) {
      // The paramaters to keep when decoupling the filter inputs from the download all button
      const paramsToConserve: (keyof ExportListParams2)[] = ['sort_by', 'sort_order', 'project_ids', 'job_ids']
      // delete any params that are not in the paramsToConserve list
      for (const param of searchParams.keys()) {
        if (!paramsToConserve.includes(param as keyof ExportListParams2)) searchParams.delete(param)
      }
    }

    searchParams.delete('limit')
    searchParams.delete('position')

    const exportUrl = createCsApiUrlFromEndpoint('jobs/export')
    exportUrl.search = searchParams.toString()

    fetchExportedJobData(exportUrl).catch(error => {
      console.error('An error occurred exporting data:', error)
    })
  }

  return (
    <>
      <WppActionButton
        loading={isExportedJobApiLoading}
        onClick={handleDownloadAllClick}
        data-testid="download-all-button"
        disabled={disabled}
      >
        <WppIconDownload slot="icon-start" />
        Download
        {/* if the url does not have "job_ids" all of the jobs are selected */}
        {!fetchUrl.searchParams.get('job_ids') && ' all'}
        {itemCount && ` (${itemCount})`}
      </WppActionButton>
    </>
  )
}

export default DownloadAllJobsButton
